import { defineStore, acceptHMRUpdate } from 'pinia';
import { createFrozenMap } from '@/stores/helpers/store.utils';
import { fetchUserJourneyTasks } from '@/stores/api/user-journey.api';
import values from 'lodash/values';

import { useSubscriptionStore } from '@/stores/subscription.store';

const pollingInterval = 6000;

let loadAllPromise: Promise<void> | null = null;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

export interface UserJourneyTask {
    id: string;
    name?: string;
    isComplete?: boolean;
    [key: string]: unknown;
}

interface UserJourneyState {
    error: unknown;
    fetching: boolean;
    map: Record<string, UserJourneyTask>;
}

export function getInitialState (): UserJourneyState {
    return {
        error: {},
        fetching: false,
        map: {},
    };
}

export const useUserJourneyStore = defineStore('userJourney', {
    state: getInitialState,
    getters: {
        list: (state): UserJourneyTask[] => values(state.map),
        getChecklistByName (): (name: string) => UserJourneyTask | undefined {
            const store = useUserJourneyStore();

            return (name: string) => store.list.find((task) => task.name === name);
        },
    },
    actions: {
        init () {
            this.startPollingForPreInstallChecklistComplete();
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            this.fetching = true;
            loadAllPromise = fetchUserJourneyTasks()
                .then(({ data: tasks }: { data: UserJourneyTask[] }) => {
                    this.fetching = false;
                    this.map = createFrozenMap(tasks) as Record<string, UserJourneyTask>;
                })
                .catch((error: unknown) => {
                    loadAllPromise = null;
                    this.fetching = false;
                    this.error = error as Record<string, unknown>;
                    throw error;
                }) as unknown as Promise<void>;

            await loadAllPromise;
        },
        async pollForPreinstallOnboardingChecklistComplete ({ intervalRef }: { intervalRef?: ReturnType<typeof setInterval> }) {
            await this.loadAll({ reFetch: true });
            const checklist = this.getChecklistByName('preinstallOnboarding');
            if (checklist?.isComplete) {
                this.onPreInstallChecklistCompletion();

                if (intervalRef) clearInterval(intervalRef);
            }
        },
        async startPollingForPreInstallOnboardingComplete () {
            await this.loadAll();

            const checklist = this.getChecklistByName('preinstallOnboarding');
            if (!checklist) return;

            if (checklist.isComplete) {
                this.onPreInstallChecklistCompletion();

                return;
            }

            const intervalRef = setInterval(() => {
                this.pollForPreinstallOnboardingChecklistComplete({ intervalRef });
            }, pollingInterval);
        },
        onPreInstallChecklistCompletion () {
            const guide = window.pendo?.guides?.find((g: { name: string; hasBeenSeen: () => boolean; show: () => void }) => g?.name === 'PLG | Trial extended from installation');

            if (guide && !guide?.hasBeenSeen()) {
                guide.show();
                useSubscriptionStore().loadActiveSubscription();
            }
        },
        startPollingForPreInstallChecklistComplete () {
            const subscriptionStore = useSubscriptionStore();
            const { featureFlags } = subscriptionStore.active || {};

            if (subscriptionStore.isInTrial && featureFlags?.hadUpfrontTrial) {
                this.startPollingForPreInstallOnboardingComplete();
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useUserJourneyStore, module.hot));
}
